import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Badge } from 'react-bootstrap';
import { Settings, Wifi, WifiOff, BookOpen, ExternalLink } from 'lucide-react';
import {
  getTenantMoodleConfig,
  saveTenantMoodleConfig,
  testTenantMoodleConnection,
  listTenantMoodleCourses,
} from '../../api/api';
import { getErrorMessage } from '../../utils/errors';

export default function TenantMoodleSettings({ tenantSlug }) {
  const [moodleUrl, setMoodleUrl] = useState('');
  const [moodleToken, setMoodleToken] = useState('');
  const [moodleEnabled, setMoodleEnabled] = useState(false);
  const [moodleConfigured, setMoodleConfigured] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantSlug) return;
    getTenantMoodleConfig(tenantSlug)
      .then((cfg) => {
        setMoodleUrl(cfg.moodle_url || '');
        setMoodleEnabled(cfg.enabled ?? false);
        setMoodleConfigured(cfg.configured ?? false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const handleSave = async () => {
    if (!moodleUrl) return;
    setSaving(true); setSaveMsg(''); setTestResult(null);
    try {
      await saveTenantMoodleConfig(tenantSlug, {
        moodle_url: moodleUrl,
        moodle_token: moodleToken || undefined,
        enabled: moodleEnabled,
      });
      setSaveMsg('Configuration saved.');
      setMoodleConfigured(true);
      setMoodleToken('');
    } catch (e) {
      setSaveMsg('Error: ' + getErrorMessage(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await testTenantMoodleConnection(tenantSlug);
      setTestResult({ ok: true, msg: `Connected — "${r.site?.sitename}" · Moodle ${r.site?.release || ''}` });
    } catch (e) {
      setTestResult({ ok: false, msg: getErrorMessage(e, 'Connection failed.') });
    } finally {
      setTesting(false);
    }
  };

  const handleLoadCourses = async () => {
    setCoursesLoading(true);
    try {
      const r = await listTenantMoodleCourses(tenantSlug);
      setCourses(r.courses || []);
    } catch (e) {
      alert('Could not load courses: ' + getErrorMessage(e, e.message));
    } finally {
      setCoursesLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" className="text-primary" />
        <div className="text-muted small mt-2">Loading Moodle configuration…</div>
      </div>
    );
  }

  return (
    <div>
      {/* Status banner */}
      <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-3"
        style={{ background: moodleConfigured && moodleEnabled ? '#f0fdf4' : '#f8f9fa', border: '1px solid', borderColor: moodleConfigured && moodleEnabled ? '#bbf7d0' : '#dee2e6' }}>
        {moodleConfigured && moodleEnabled
          ? <Wifi size={22} className="text-success flex-shrink-0" />
          : <WifiOff size={22} className="text-secondary flex-shrink-0" />}
        <div className="flex-grow-1">
          <div className="fw-semibold small" style={{ color: moodleConfigured && moodleEnabled ? '#15803d' : '#495057' }}>
            {moodleConfigured && moodleEnabled ? 'Moodle integration is active' : 'Moodle integration is not configured'}
          </div>
          {moodleUrl && (
            <div className="x-small text-muted mt-1 d-flex align-items-center gap-1">
              {moodleUrl}
              <a href={moodleUrl} target="_blank" rel="noopener noreferrer" className="text-muted ms-1">
                <ExternalLink size={11} />
              </a>
            </div>
          )}
        </div>
        <Badge bg={moodleEnabled ? 'success' : 'secondary'} className="rounded-pill">
          {moodleEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {/* Configuration form */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Header className="bg-white border-bottom py-3 d-flex align-items-center gap-2">
          <Settings size={16} className="text-primary" />
          <span className="fw-bold small text-uppercase ls-1 text-primary">Connection Settings</span>
        </Card.Header>
        <Card.Body className="p-4">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-muted">Moodle Site URL</label>
              <input
                className="form-control form-control-sm"
                placeholder="https://moodle.example.edu"
                value={moodleUrl}
                onChange={(e) => setMoodleUrl(e.target.value)}
              />
              <div className="form-text">Include the protocol (https://)</div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-muted">Web Service Token</label>
              <input
                className="form-control form-control-sm"
                type="password"
                placeholder={moodleConfigured ? '••••••• (saved — paste to change)' : 'Paste token from Site Admin → Web Services'}
                value={moodleToken}
                onChange={(e) => setMoodleToken(e.target.value)}
              />
              <div className="form-text">Site Administration → Plugins → Web Services → Manage Tokens</div>
            </div>
          </div>

          <div className="form-check form-switch mb-4">
            <input
              className="form-check-input"
              type="checkbox"
              id="moodleEnabledSwitch"
              checked={moodleEnabled}
              onChange={(e) => setMoodleEnabled(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label className="form-check-label small fw-semibold" htmlFor="moodleEnabledSwitch">
              Enable Moodle integration for this tenant
            </label>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={handleSave} disabled={saving || !moodleUrl}>
              {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving…</> : 'Save Configuration'}
            </Button>
            {moodleConfigured && (
              <Button variant="outline-success" size="sm" className="rounded-pill px-3" onClick={handleTest} disabled={testing}>
                {testing ? <><Spinner size="sm" animation="border" className="me-1" /> Testing…</> : 'Test Connection'}
              </Button>
            )}
            {moodleConfigured && (
              <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={handleLoadCourses} disabled={coursesLoading}>
                {coursesLoading ? <><Spinner size="sm" animation="border" className="me-1" /> Loading…</> : <><BookOpen size={13} className="me-1" />Load Courses</>}
              </Button>
            )}
            {saveMsg && (
              <span className={'small fw-semibold ms-1 ' + (saveMsg.startsWith('Error') ? 'text-danger' : 'text-success')}>
                {!saveMsg.startsWith('Error') && '✓ '}{saveMsg}
              </span>
            )}
          </div>

          {testResult && (
            <div className={'mt-3 px-3 py-2 rounded-3 small fw-semibold ' + (testResult.ok ? 'text-success bg-success bg-opacity-10 border border-success border-opacity-25' : 'text-danger bg-danger bg-opacity-10 border border-danger border-opacity-25')}>
              {testResult.ok ? '✓ ' : '✗ '}{testResult.msg}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Course list */}
      {courses.length > 0 && (
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Header className="bg-white border-bottom py-3 d-flex align-items-center gap-2">
            <BookOpen size={16} className="text-success" />
            <span className="fw-bold small text-uppercase ls-1 text-success">Available Courses</span>
            <Badge bg="success" className="rounded-pill ms-auto">{courses.length}</Badge>
          </Card.Header>
          <Card.Body className="p-4">
            <div className="row g-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {courses.map((c) => (
                <div key={c.id} className="col-md-4 col-sm-6">
                  <div className="border rounded-3 px-3 py-2 h-100" style={{ fontSize: 13 }}>
                    <div className="fw-semibold text-truncate">{c.fullname}</div>
                    <div className="text-muted x-small">ID: {c.id}{c.shortname ? ` · ${c.shortname}` : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
